import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, MapPin, RefreshCw, Search, X } from 'lucide-react';
import { Button, Loading } from '@/components/ui';
import { DonorCard } from '@/components/search/DonorCard';
import { SearchFilters } from '@/components/search/SearchFilters';
import { ProfileBottomSheet } from '@/components/profile/ProfileBottomSheet';
import { useSearchStore, type SearchFilters as SearchFilterValues } from '@/stores/searchStore';
import { useProfileStore } from '@/stores/profileStore';
import { useAuthStore } from '@/stores/authStore';
import { trackEvent } from '@/lib/analytics';

const sortLabels: Record<string, string> = {
  highSpec: 'ハイスペック順',
  review: 'レビュー順',
  verified: '認証充実順',
  nearby: '近い順',
  newest: '新着順',
};

const incomeMinLabels: Record<string, string> = {
  '400万円〜600万円': '400万円以上',
  '600万円〜800万円': '600万円以上',
  '800万円〜1000万円': '800万円以上',
  '1000万円〜1500万円': '1000万円以上',
  '1500万円〜2000万円': '1500万円以上',
  '2000万円以上': '2000万円以上',
};

function countActiveFilters(filters: SearchFilterValues): number {
  return Object.values(filters).filter(Boolean).length;
}

function trackDonorSearchStart(filters: SearchFilterValues, source: string): void {
  const filterCount = countActiveFilters(filters);
  trackEvent('donor_search_start', {
    source,
    filter_count: filterCount,
    has_filters: filterCount > 0,
    sort_by: filters.sortBy || 'recommended',
  });
}

export function SearchPage() {
  const { user } = useAuthStore();
  const { profile, fetchProfile } = useProfileStore();
  const {
    donors,
    filters,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    searchDonors,
    loadMoreDonors,
    setDoneePrefecture,
    setFilters,
  } = useSearchStore();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProfileUserId, setSelectedProfileUserId] = useState<string | null>(null);
  const didTrackInitialSearchRef = useRef(false);

  // Fetch profile to get donee's prefecture for scoring
  useEffect(() => {
    if (user?.uid && !profile) {
      fetchProfile(user.uid);
    }
  }, [user?.uid, profile, fetchProfile]);

  // Set donee's prefecture for scoring
  useEffect(() => {
    if (profile?.basicInfo?.residence) {
      setDoneePrefecture(profile.basicInfo.residence);
    }
  }, [profile?.basicInfo?.residence, setDoneePrefecture]);

  // Initial search
  useEffect(() => {
    if (!didTrackInitialSearchRef.current) {
      trackDonorSearchStart(useSearchStore.getState().filters, 'initial');
      didTrackInitialSearchRef.current = true;
    }
    searchDonors();
  }, [searchDonors]);

  const handleSearch = () => {
    trackDonorSearchStart(filters, 'manual');
    void searchDonors();
  };

  const handleFilterApply = () => {
    trackDonorSearchStart(filters, 'filter_apply');
    void searchDonors();
  };

  const activeFilterCount = countActiveFilters(filters);
  const removeFilter = useCallback((patch: Partial<SearchFilterValues>) => {
    trackDonorSearchStart({ ...filters, ...patch }, 'filter_remove');
    setFilters(patch);
    void searchDonors();
  }, [filters, searchDonors, setFilters]);
  const activeFilterChips = useMemo(
    () =>
      [
        filters.region
          ? {
              key: 'region',
              label: filters.region,
              onRemove: () => removeFilter({ region: undefined }),
            }
          : null,
        filters.prefecture
          ? {
              key: 'prefecture',
              label: filters.prefecture,
              onRemove: () => removeFilter({ prefecture: undefined }),
            }
          : null,
        typeof filters.ageMin === 'number'
          ? {
              key: 'ageMin',
              label: `${filters.ageMin}歳〜`,
              onRemove: () => removeFilter({ ageMin: undefined }),
            }
          : null,
        typeof filters.ageMax === 'number'
          ? {
              key: 'ageMax',
              label: `〜${filters.ageMax}歳`,
              onRemove: () => removeFilter({ ageMax: undefined }),
            }
          : null,
        typeof filters.heightMin === 'number'
          ? {
              key: 'heightMin',
              label: `${filters.heightMin}cm〜`,
              onRemove: () => removeFilter({ heightMin: undefined }),
            }
          : null,
        typeof filters.heightMax === 'number'
          ? {
              key: 'heightMax',
              label: `〜${filters.heightMax}cm`,
              onRemove: () => removeFilter({ heightMax: undefined }),
            }
          : null,
        filters.educationLevel
          ? {
              key: 'educationLevel',
              label: filters.educationLevel,
              onRemove: () => removeFilter({ educationLevel: undefined }),
            }
          : null,
        filters.bloodType
          ? {
              key: 'bloodType',
              label: `${filters.bloodType}型`,
              onRemove: () => removeFilter({ bloodType: undefined }),
            }
          : null,
        filters.donationMethod === 'syringe'
          ? {
              key: 'donationMethod',
              label: 'シリンジ法',
              onRemove: () => removeFilter({ donationMethod: undefined }),
            }
          : filters.donationMethod === 'timing'
            ? {
                key: 'donationMethod',
                label: 'タイミング法',
                onRemove: () => removeFilter({ donationMethod: undefined }),
              }
            : null,
        filters.sortBy
          ? {
              key: 'sortBy',
              label: sortLabels[filters.sortBy] || 'おすすめ順',
              onRemove: () => removeFilter({ sortBy: undefined }),
            }
          : null,
        filters.annualIncomeMin
          ? {
              key: 'annualIncomeMin',
              label: incomeMinLabels[filters.annualIncomeMin] || `${filters.annualIncomeMin}以上`,
              onRemove: () => removeFilter({ annualIncomeMin: undefined }),
            }
          : null,
        filters.minReviewRating
          ? {
              key: 'minReviewRating',
              label: `★${filters.minReviewRating.toFixed(1)}以上`,
              onRemove: () => removeFilter({ minReviewRating: undefined }),
            }
          : null,
        filters.hasReviews
          ? {
              key: 'hasReviews',
              label: 'レビューあり',
              onRemove: () => removeFilter({ hasReviews: undefined }),
            }
          : null,
        filters.identityVerified
          ? {
              key: 'identityVerified',
              label: '本人認証済み',
              onRemove: () => removeFilter({ identityVerified: undefined }),
            }
          : null,
        filters.educationVerified
          ? {
              key: 'educationVerified',
              label: '学歴証明済み',
              onRemove: () => removeFilter({ educationVerified: undefined }),
            }
          : null,
        filters.incomeVerified
          ? {
              key: 'incomeVerified',
              label: '年収証明済み',
              onRemove: () => removeFilter({ incomeVerified: undefined }),
            }
          : null,
        filters.spermTestVerified
          ? {
              key: 'spermTestVerified',
              label: '精液検査済み',
              onRemove: () => removeFilter({ spermTestVerified: undefined }),
            }
          : null,
        filters.stdTestVerified
          ? {
              key: 'stdTestVerified',
              label: '性感染症検査済み',
              onRemove: () => removeFilter({ stdTestVerified: undefined }),
            }
          : null,
        filters.donationArea
          ? {
              key: 'donationArea',
              label: `${filters.donationArea}で提供可`,
              onRemove: () => removeFilter({ donationArea: undefined }),
            }
          : null,
        filters.travelExpense
          ? {
              key: 'travelExpense',
              label: filters.travelExpense,
              onRemove: () => removeFilter({ travelExpense: undefined }),
            }
          : null,
        filters.identityDisclosure
          ? {
              key: 'identityDisclosure',
              label: filters.identityDisclosure,
              onRemove: () => removeFilter({ identityDisclosure: undefined }),
            }
          : null,
        filters.bodyType
          ? {
              key: 'bodyType',
              label: filters.bodyType,
              onRemove: () => removeFilter({ bodyType: undefined }),
            }
          : null,
        filters.maritalStatus
          ? {
              key: 'maritalStatus',
              label: filters.maritalStatus,
              onRemove: () => removeFilter({ maritalStatus: undefined }),
            }
          : null,
      ].filter(
        (chip): chip is { key: string; label: string; onRemove: () => void } => chip !== null
      ),
    [filters, removeFilter]
  );
  const visibleFilterChips = activeFilterChips.slice(0, 4);
  const hiddenFilterChipCount = Math.max(activeFilterChips.length - visibleFilterChips.length, 0);

  return (
    <div className="min-h-full bg-[#F7F4EF] pb-8">
      <div className="md:mx-auto md:max-w-6xl md:px-6 md:py-6">
        <div className="px-4 pb-3 pt-14 md:px-0 md:pt-0">
          <h1 className="mb-4 text-[22px] font-semibold text-[#1A1A1A]">検索</h1>
          <div className="flex items-center gap-2.5 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button
              type="button"
              onClick={() => setIsFilterOpen(true)}
              className={`relative flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[12px] font-medium transition-colors ${
                activeFilterCount > 0
                  ? 'border-[#B87333] bg-[#B87333] text-white'
                  : 'border-[#DEDBD6] bg-white text-[#555]'
              }`}
            >
              <Filter size={13} className="shrink-0" />
              フィルター
              {activeFilterCount > 0 && (
                <span className="ml-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white text-[10px] font-bold text-[#B87333]">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {visibleFilterChips.map((chip) => (
              <div
                key={`${chip.key}-${chip.label}`}
                className="flex h-8 shrink-0 items-center rounded-full border border-[#E0C9A8] bg-[#F5EDE2] pl-3 text-[12px] font-medium text-[#B87333]"
              >
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(true)}
                  className="max-w-[8rem] truncate whitespace-nowrap text-left"
                >
                  {chip.label}
                </button>
                <button
                  type="button"
                  onClick={chip.onRemove}
                  className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-[#B87333] transition hover:bg-[#EADBC7]"
                  aria-label={`${chip.label} を解除`}
                >
                  <X size={11} className="shrink-0" />
                </button>
              </div>
            ))}

            {hiddenFilterChipCount > 0 && (
              <button
                type="button"
                onClick={() => setIsFilterOpen(true)}
                className="flex h-8 shrink-0 items-center rounded-full border border-[#DEDBD6] bg-white px-3 text-[12px] font-medium text-[#777]"
              >
                +{hiddenFilterChipCount}
              </button>
            )}
          </div>
        </div>

        {profile?.basicInfo?.residence && (
          <div className="mx-4 mb-3 flex items-center gap-2 rounded-xl bg-[#F5EDE2] px-3 py-2 text-[12px] text-[#7A6A53] md:mx-0">
            <MapPin className="h-4 w-4 text-copper" />
            <span>
              あなたの地域: <span className="font-medium text-[#6A5538]">{profile.basicInfo.residence}</span>
            </span>
          </div>
        )}

        {error && (
          <div className="mx-4 mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700 md:mx-0">
            {error}{' '}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSearch}
              className="ml-2 !px-3 !py-1.5"
            >
              再試行
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loading />
          </div>
        )}

        {!isLoading && donors.length > 0 && (
          <>
            <div className="mb-2 flex items-center justify-between px-5 md:px-0">
              <p className="text-[12px] text-[#AAA]">
                {donors.length}件のドナー
              </p>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                onClick={handleSearch}
                className="!px-2 !py-1 text-[11px]"
              >
                更新
              </Button>
            </div>

            <div
              className="mx-4 overflow-hidden rounded-2xl bg-white md:hidden"
              style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)' }}
            >
              {donors.map((donor, index) => (
                <div key={donor.userId}>
                  <DonorCard donor={donor} onOpenProfile={setSelectedProfileUserId} />
                  {index < donors.length - 1 && <div className="ml-[80px] h-px bg-[#F0EDE8]" />}
                </div>
              ))}
            </div>

            <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-3">
              {donors.map((donor) => (
                <div
                  key={donor.userId}
                  className="overflow-hidden rounded-2xl bg-white"
                  style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)' }}
                >
                  <DonorCard donor={donor} onOpenProfile={setSelectedProfileUserId} />
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="mt-5 flex justify-center px-4 md:px-0">
                <Button
                  variant="secondary"
                  onClick={loadMoreDonors}
                  isLoading={isLoadingMore}
                  className="!rounded-2xl !px-6 !py-3"
                >
                  もっと見る
                </Button>
              </div>
            )}
          </>
        )}

        {!isLoading && donors.length === 0 && !error && (
          <div className="px-8 py-20 text-center">
            <Search className="mx-auto mb-4 h-14 w-14 text-[#D0CCC4]" />
            <h2 className="mb-2 text-[15px] font-medium text-[#999]">
              ドナーが見つかりませんでした
            </h2>
            <p className="mx-auto mb-5 max-w-md text-[13px] text-[#B0AAA0]">
              条件に合うドナーがいません
            </p>
            <div className="flex flex-col items-center gap-3">
              {hasMore && (
                <Button
                  variant="secondary"
                  onClick={loadMoreDonors}
                  isLoading={isLoadingMore}
                  className="!rounded-2xl !px-6 !py-3"
                >
                  さらに探す
                </Button>
              )}
              <button
                type="button"
                onClick={() => setIsFilterOpen(true)}
                className="text-[13px] font-medium text-[#B87333]"
              >
                フィルターを変更
              </button>
            </div>
          </div>
        )}
      </div>

      <SearchFilters
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleFilterApply}
      />

      <ProfileBottomSheet
        userId={selectedProfileUserId}
        isOpen={selectedProfileUserId !== null}
        onClose={() => setSelectedProfileUserId(null)}
      />
    </div>
  );
}
